import { useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { NavHeader } from './NavHeader';

const PAGE_SIZE = 12;

/**
 * Year view — 3×4 grid of year buttons displayed in 12-year pages.
 *
 * Accessibility: role="grid" semantics with a roving tabIndex. Arrow keys move
 * focus within the visible page (Left/Right by one, Up/Down by a row), Home/End
 * jump to the first/last year, and PageUp/PageDown step a page.
 */
export function YearPicker({ selectedDate, onSelect, maxDate, yearsWithData = [], onViewPageChange }) {
    const today = dayjs();

    const [firstYearOnPage, setFirstYearOnPage] = useState(
        Math.floor(selectedDate.year() / PAGE_SIZE) * PAGE_SIZE
    );
    const [focusedIdx, setFocusedIdx] = useState(
        Math.min(Math.max(selectedDate.year() - Math.floor(selectedDate.year() / PAGE_SIZE) * PAGE_SIZE, 0), PAGE_SIZE - 1)
    );
    const gridRef = useRef(null);

    const years = Array.from({ length: PAGE_SIZE }, (_, i) => firstYearOnPage + i);
    const disableNext = maxDate ? firstYearOnPage + PAGE_SIZE > maxDate.year() : false;

    const isYearDisabled = (yr) => (maxDate ? yr > maxDate.year() : false);

    // flushSync commits the state change before we focus, so the target year
    // cell is in the DOM when querySelector runs.
    const focusYear = (idx) => {
        gridRef.current?.querySelector(`[data-year-idx="${idx}"]`)?.focus();
    };

    const navigatePage = (newPageStart) => {
        setFirstYearOnPage(newPageStart);
        onViewPageChange?.(newPageStart);
    };

    const moveFocus = (idx) => {
        if (idx < 0 || idx > PAGE_SIZE - 1 || isYearDisabled(firstYearOnPage + idx)) return;
        flushSync(() => setFocusedIdx(idx));
        focusYear(idx);
    };

    // Keyboard paging: year cells are keyed by year value, so the focused cell's
    // DOM node is replaced on a page change and focus would be lost. Re-focus the
    // same grid position on the new page explicitly.
    const pageByKeyboard = (newPageStart) => {
        flushSync(() => navigatePage(newPageStart));
        focusYear(focusedIdx);
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowLeft': moveFocus(focusedIdx - 1); break;
            case 'ArrowRight': moveFocus(focusedIdx + 1); break;
            case 'ArrowUp': moveFocus(focusedIdx - 3); break;
            case 'ArrowDown': moveFocus(focusedIdx + 3); break;
            case 'Home': moveFocus(0); break;
            case 'End': moveFocus(PAGE_SIZE - 1); break;
            case 'PageUp': pageByKeyboard(firstYearOnPage - PAGE_SIZE); break;
            case 'PageDown': if (!disableNext) pageByKeyboard(firstYearOnPage + PAGE_SIZE); break;
            default: return;
        }
        e.preventDefault();
    };

    return (
        <div className="p-3 w-[280px]">
            <NavHeader
                label={String(selectedDate.year())}
                onPrev={() => navigatePage(firstYearOnPage - PAGE_SIZE)}
                onNext={() => navigatePage(firstYearOnPage + PAGE_SIZE)}
                disableNext={disableNext}
                prevLabel="Previous years"
                nextLabel="Next years"
            />
            <div
                ref={gridRef}
                role="grid"
                aria-label={`Years ${firstYearOnPage}–${firstYearOnPage + PAGE_SIZE - 1}`}
                className="mt-4 grid grid-cols-3 gap-2"
                onKeyDown={handleKeyDown}
            >
                {years.map((yr, idx) => {
                    const isSelected = selectedDate.year() === yr;
                    const isCurrentYear = today.year() === yr;
                    const isDisabled = isYearDisabled(yr);
                    const isHighlighted = yearsWithData.includes(yr);

                    return (
                        <div key={yr} role="gridcell" aria-selected={isSelected} className="contents">
                            <button
                                type="button"
                                tabIndex={idx === focusedIdx ? 0 : -1}
                                onClick={() =>
                                    onSelect(dayjs().year(yr).startOf('year'))
                                }
                                disabled={isDisabled}
                                aria-disabled={isDisabled}
                                aria-current={isCurrentYear ? 'date' : undefined}
                                aria-label={String(yr)}
                                data-year-idx={idx}
                                className={cn(
                                    'relative flex h-9 flex-col items-center justify-center rounded-md text-sm transition-colors',
                                    'hover:bg-accent/20 hover:text-foreground',
                                    isSelected &&
                                        'bg-accent text-accent-foreground hover:bg-accent',
                                    isCurrentYear && !isSelected && 'bg-muted text-foreground',
                                    isDisabled && 'pointer-events-none opacity-30'
                                )}
                            >
                                <span className={isHighlighted ? '-translate-y-px' : ''}>
                                    {yr}
                                </span>
                                {isHighlighted && (
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            'absolute bottom-1 h-1 w-1 rounded-full',
                                            isSelected ? 'bg-accent-foreground' : 'bg-accent'
                                        )}
                                    />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
